import React, { Fragment, useState } from 'react';
import { ActionStyles, AdminTable } from '../Admin/reusableStyles';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { BiArchiveIn, BiArchiveOut } from 'react-icons/bi';
import PromptTemplateForm from './PromptTemplateForm';
import Swal from 'sweetalert2';
import { archivePromptTemplate, createPromptTemplate, unarchivePromptTemplate } from '@/_services/api/promptTemplate';
import { toast } from 'react-toastify';
import { VscLoading } from 'react-icons/vsc';
import { getTeamIdFromURL } from '@/_helpers';
import { AiOutlineCopy } from 'react-icons/ai';

export default function PromptTemplateTable({ promptTemplates, onEdit, onArchive, page, limit }) {
    const [isBusy, setIsBusy] = useState(null);
    const teamId = getTeamIdFromURL();

    const handleArchive = async (template) => {
        try {
            const confirm = await Swal.fire({
                title: `Are you sure you want to archive prompt template${template?.name || ''}?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Confirm',
            });
            if (confirm) {
                const promptTemplateId = template._id;
                setIsBusy(promptTemplateId);
                await archivePromptTemplate({ promptTemplateId });
                await onArchive();
            }
            toast.success('Prompt template archived successfully!');
        } catch (error) {
            toast.error(error?.message || 'Something went wrong!');
        } finally {
            setIsBusy(false);
        }
    };

    const handleUnarchive = async (template) => {
        try {
            const confirm = await Swal.fire({
                title: `Are you sure you want to unarchive prompt template ${template?.name || ''}?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Confirm',
            });
            if (confirm) {
                const promptTemplateId = template._id;
                setIsBusy(promptTemplateId);
                await unarchivePromptTemplate({ promptTemplateId });
                await onArchive();
            }
            toast.success('Prompt template unarchived successfully!');
        } catch (error) {
            toast.error(error?.message || 'Something went wrong!');
        } finally {
            setIsBusy(false);
        }
    };

    const copyExistingTemplate = async (template) => {
        try {
            const confirm = await Swal.fire({
                title: `Are you sure you want to copy :${template?.name || ''}?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Confirm',
            });
            if (confirm) {
                const preparedFormData = {
                    name: template.name,
                    shortcutKeyword: template.shortcutKeyword + '-copy',
                    description: template.description,
                    promptText: template.promptText,
                };
                if (teamId) {
                    preparedFormData.team = teamId;
                } else {
                    preparedFormData.teams = template?.teams || [];
                }
                await createPromptTemplate({ data: preparedFormData });
                await onArchive();
                toast.success('Prompt template copied successfully!');
            }
        } catch (error) {
            toast.error(error?.message || 'Something went wrong!');
        } finally {
            setIsBusy(false);
        }
    };
    return (
        <AdminTable>
            <thead>
                <tr>
                    <th>SN</th>
                    <th>Name</th>
                    <th>Team / Library</th>
                    <th>Shortcut Keyword</th>
                    <th style={{ width: '120px' }}>Actions</th>
                </tr>
            </thead>
            <tbody>
                {promptTemplates.map((template, index) => {
                    const canUpdate = template?.team || !teamId;
                    return (
                        <tr key={template._id}>
                            <td width="30px">{(page - 1) * limit + index + 1}.</td>
                            <td>
                                {template.name}
                                {template?.archived && <span className="badge badge-danger ml-2">Archived</span>}
                            </td>
                            <td>
                                <code>
                                    {template?.teamData ? (
                                        <span className="badge badge-info">{template?.teamData?.name}</span>
                                    ) : (
                                        <span className="badge badge-warning">Prompt Template Library</span>
                                    )}
                                </code>
                            </td>
                            <td>
                                <code>{template.shortcutKeyword}</code>
                            </td>
                            <td>
                                <ActionStyles>
                                    <PromptTemplateForm template={template} viewMode={true} />
                                    {canUpdate && (
                                        <Fragment>
                                            <PromptTemplateForm template={template} onEdit={onEdit} />
                                            {isBusy === template._id ? (
                                                <VscLoading className="spin" size={24} />
                                            ) : (
                                                <Fragment>
                                                    {template.archived ? (
                                                        <BootstrapToolTip text="Unarchive">
                                                            <BiArchiveOut
                                                                onClick={() => handleUnarchive(template)}
                                                                className="icon assign"
                                                            />
                                                        </BootstrapToolTip>
                                                    ) : (
                                                        <BootstrapToolTip text="Archive">
                                                            <BiArchiveIn
                                                                onClick={() => handleArchive(template)}
                                                                className="icon archiveIn"
                                                            />
                                                        </BootstrapToolTip>
                                                    )}
                                                </Fragment>
                                            )}
                                        </Fragment>
                                    )}
                                    <BootstrapToolTip text="Copy Prompt Template">
                                        <span
                                            onClick={() => copyExistingTemplate(template)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {isBusy ? (
                                                <VscLoading className="spin" />
                                            ) : (
                                                <AiOutlineCopy className="icon" />
                                            )}
                                        </span>
                                    </BootstrapToolTip>
                                </ActionStyles>
                                <div className="d-flex"></div>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </AdminTable>
    );
}
